import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';

import { useHistory, useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';

import { Box, CircularProgress, Drawer, Grid, makeStyles, TextField } from '@material-ui/core';

import notification from 'app/config/notification';
import lineEndpoint from 'app/api/services/lineEndpoint';
import { errorHandler } from 'app/utils/errorHandler';
import { validateFormikForm } from 'app/utils/forms/functions';

import { conductorInitialValues } from '../form-configs/conductor-initial-values';
import { conductorsValidationSchema } from '../form-configs/validation-schemas';
import { LINES_SITUATIONS, SERVICES_CODES } from 'app/config/services';
import { URLS } from 'app/config/urls';
import helpers from 'app/utils/helpers';

import { clearFiles, setBoExcluidoByIndex, setFiles } from 'app/store/slices/forms';
import { clearDuplicatedRegistry, fetchLine, setConductorDefaultExpirationDate } from 'app/store/slices/line';

import Loading from 'app/layouts/Loading';
import PageWrapper from '../components/PageWrapper';
import SaveBox from '../components/SaveBox';
import CustomTextField from '../components/TextField';
import CustomDateField from '../components/DateField';
import OtherDocuments from 'app/components/OtherDocuments';
import CustomMaskField from '../components/MaskField';
import DefaultTable from '../components/DefaultTable';
import AddButton from '../components/AddButton';
import ConductorModal from './modals/ConductorModal';
import FindPermissionary from './drawers/FindPermissionary';
import RequestProcess from '../components/RequestProcess';

import styles from './styles';
import { conductorDetailsPermissionariesTable } from '../table-configs/columns-schemas';
import ConductorImage from '../components/ConductorImage';
import { DOCUMENT_TYPES } from '../../../utils/constants';

const useStyles = makeStyles(styles);

const ConductorsDetails = (props) => {
    const history = useHistory();
    const match = useRouteMatch();
    const dispatch = useDispatch();
    const classes = useStyles();

    const cdLinhaFromPath = React.useRef(match.params.cdLinha).current;
    const cdConductorFromPath = React.useRef(match.params.cdCondutorSubstituto).current;
    const defaultExpirationDate = useSelector((state) => state.line.conductorDefaultExpirationDate);

    const servicesFilter = useRef([{ cdServico: SERVICES_CODES.SLIC_INCLUSAO_CONDUTOR_SUBSTITUTO }]).current;

    const [loading, setLoading] = useState(true);
    const [loadingLine, setLoadingLine] = useState(true);
    const [loadingRequest, setLoadingRequest] = useState(true);
    const [requestProcessValues, setRequestProcessValues] = useState({});

    const [permissionaries, setPermissionaries] = useState([]);
    const [rowsPermissionaries, setRowsPermissionaries] = useState([]);

    const [openPermissionaryDrawer, setOpenPermissionaryDrawer] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [modalData, setModalData] = useState({});

    const [selectedConductor, setSelectedConductor] = useState(null);
    const [carteiraCondutor, setCarteiraCondutor] = useState('');
    const [formInitials, setFormInitials] = useState(() => {
        const temp = { ...conductorInitialValues };
        if (temp.dtEmissao != null && !temp.dtValidade) {
            temp.dtValidade = moment(temp.dtEmissao).add(defaultExpirationDate, 'y');
        }
        return temp;
    });
    const [othersDocuments, setOthersDocuments] = useState([]);
    const [loadingDocuments, setLoadingDocuments] = useState(false);

    const selectedLineStore = useSelector((state) => state.line.selected);
    const duplicatedConductor = useSelector((state) => state.line.duplicatedRegistry);

    const canEdit = useMemo(() => {
        return !(
            selectedConductor &&
            selectedConductor.cdLinhaSituacao &&
            selectedConductor.cdLinhaSituacao !== LINES_SITUATIONS.EM_REVISAO.code
        );
    }, [selectedConductor]);

    useEffect(() => {
        (async () => {
            setLoadingLine(true);
            if (!defaultExpirationDate || defaultExpirationDate === '') {
                const response = await lineEndpoint.getDefaultExpirationDate();
                dispatch(setConductorDefaultExpirationDate(response?.data));
            }
            setLoadingLine(false);
        })();
        return () => {
            dispatch(clearFiles());
            dispatch(clearDuplicatedRegistry());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        (async () => {
            setLoadingLine(true);
            if (!selectedLineStore && cdLinhaFromPath) {
                await dispatch(fetchLine(cdLinhaFromPath));
            }
            setLoadingLine(false);
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLineStore]);

    useEffect(() => {
        (() => {
            if (duplicatedConductor) {
                setLoading(true);
                const conductor = _.cloneDeep(duplicatedConductor);
                conductor.cdCondutorSubstituto = null;
                conductor.dtEmissao = new Date();
                conductor.dtValidade = moment(new Date()).add(defaultExpirationDate, 'y');
                setSelectedConductor(conductor);
                setFormInitials({ ...conductorInitialValues, ...conductor });
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duplicatedConductor]);

    useEffect(() => {
        (async () => {
            setLoadingDocuments(true);
            try {
                if (cdConductorFromPath) {
                    if (!selectedConductor) {
                        setLoading(true);
                        const response = await lineEndpoint.getSubstituteConductor(cdConductorFromPath);
                        if (response && response.status === 200) {
                            const conductor = response.data;
                            const nuCondutor =
                                conductor?.nuCondutorFmt ||
                                `${conductor?.nuCondutor.toString().padStart(3, '0')}/${conductor?.nuAno}`;
                            setSelectedConductor(conductor);
                            setCarteiraCondutor(() => nuCondutor);
                            setFormInitials({ ...conductorInitialValues, ...conductor });
                        }
                    }
                } else {
                    const temp = { ...conductorInitialValues };
                    if (temp.dtEmissao != null && !temp.dtValidade) {
                        temp.dtValidade = moment(temp.dtEmissao).add(defaultExpirationDate, 'y');
                    }
                    setFormInitials(temp);
                }
                setLoadingDocuments(false);
                setLoading(false);
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
                setLoadingDocuments(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cdConductorFromPath, selectedLineStore]);

    useEffect(() => {
        (() => {
            if (selectedConductor?.permissionarios) {
                setPermissionaries(selectedConductor.permissionarios);
            }
            if (selectedConductor?.documentos) {
                const temp = selectedConductor.documentos.map((doc) => {
                    const t = { ...doc };
                    t.canEdit = canEdit;
                    return t;
                });
                setOthersDocuments(temp);
                dispatch(setFiles(temp));
            }
        })();
    }, [canEdit, dispatch, selectedConductor]);

    useEffect(() => {
        (() => {
            let permi = permissionaries.filter((p) => !p.boExcluido);
            setRowsPermissionaries(permi);
        })();
    }, [permissionaries]);

    const handleGenerateDocument = (data) => {
        setModalData({
            line: selectedLineStore,
            conductor: data,
            requestProcessValues: requestProcessValues,
        });
        setOpenModal(true);
    };

    const updateConductor = (data) => {
        setSelectedConductor(data);
        const nuCondutorFmt = data.condutorSubstituto && data.condutorSubstituto.nuCondutorFmt;
        const nuCondutor = nuCondutorFmt || `${data?.nuCondutor.toString().padStart(3, '0')}/${data?.nuAno}`;
        setCarteiraCondutor(nuCondutor);
        setFormInitials({ ...conductorInitialValues, ...data });
    };

    const handleSubmitForm = async (values, formikHelpers) => {
        try {
            setLoading(true);
            const valuesCopy = _.cloneDeep(values);

            const dtEmissao = moment(valuesCopy.dtEmissao);
            const dtValidade = moment(valuesCopy.dtValidade);
            if (dtEmissao.isAfter(dtValidade)) {
                notification('A data de emissão deve ser anterior ou igual a data de validade.', {
                    toastId: 'VALIDATION',
                    type: 'error',
                });
                return;
            }
            if (
                requestProcessValues.nuProcesso &&
                requestProcessValues.nuProcesso !== '' &&
                requestProcessValues.processError
            ) {
                notification('O número do processo não existe no CPA', {
                    toastId: 'VALIDATION',
                    type: 'error',
                });
                if (formikHelpers) {
                    formikHelpers.setFieldError('nuProcesso', 'O número do processo não existe no CPA');
                }
            }

            valuesCopy.cdLinha = selectedLineStore.cdLinha;
            valuesCopy.nuProcesso = requestProcessValues.nuProcesso;
            valuesCopy.nuSolicitacao = requestProcessValues.nuSolicitacaoFmt;
            valuesCopy.cdProcesso = requestProcessValues.cdProcesso;
            valuesCopy.cdSolicitacao = requestProcessValues.cdSolicitacao;
            valuesCopy.permissionarios = permissionaries;
            valuesCopy.documentos = othersDocuments;

            if (!valuesCopy.cdCondutorSubstituto) valuesCopy.boAtivo = true;

            if (valuesCopy.deCpf) valuesCopy.deCpf = helpers.somenteAlfanumericos(valuesCopy.deCpf);
            const saved = await lineEndpoint.saveSubstituteConductor(valuesCopy);
            if (saved.status === 200) {
                notification('Condutor Substituto salvo com sucesso', {
                    toastId: `VALIDATION`,
                    type: 'success',
                });

                const response = await lineEndpoint.getSubstituteConductor(saved.data.cdCondutorSubstituto);
                if (response && response.status === 200) {
                    updateConductor(response.data);
                }

                if (!cdConductorFromPath) {
                    const url = generatePath(URLS.LINES_MANAGE_SCHEDULES_EDIT, {
                        cdLinha: cdLinhaFromPath,
                        cdCondutorSubstituto: saved.data.cdCondutorSubstituto,
                    });
                    history.push(url);
                }
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        } finally {
            setLoading(false);
        }
    };

    const validateDocument = (value) => {
        let error;
        if (!helpers.validateCpfCnpj(value)) {
            error = 'Documento inválido';
        }
        return error;
    };

    const handleSetSelectedPermissionary = (permissionaryData) => {
        const tempPermissionaries = [...permissionaries];
        permissionaryData.forEach((permissionary) => {
            const foundIndex = tempPermissionaries.findIndex(
                (p) => p.cdLinhaPermissionario === permissionary.cdLinhaPermissionario,
            );
            if (foundIndex >= 0) {
                tempPermissionaries[foundIndex].boExcluido = false;
                return;
            }
            tempPermissionaries.push(permissionary);
        });
        setPermissionaries(tempPermissionaries);
    };

    const handleDeletePermissionary = (event, permissionaryData) => {
        if (window.confirm('Deseja excluir o permissionário da lista?')) {
            const permissionariesTemp = permissionaries.map((permissionary) => {
                const temp = { ...permissionary };
                if (permissionary.cdLinhaPermissionario === permissionaryData.cdLinhaPermissionario) {
                    temp.boExcluido = true;
                }
                return temp;
            });
            setPermissionaries(permissionariesTemp);
        }
    };

    const customDeleteDocuments = async ({ documentsList, index }) => {
        const tempDocuments = [...documentsList];
        if (tempDocuments[index]?.idFile) {
            tempDocuments[index] = { ...tempDocuments[index], boExcluido: true };
            await dispatch(setBoExcluidoByIndex({ index }));
        } else {
            tempDocuments.splice(index, 1);
        }
        return tempDocuments;
    };

    const callBackRequest = async (request, fromClick, formikBag) => {
        setLoadingRequest(true);
        if (request && request.cdSolicitacao !== '') {
            const jsonSolicitacao = JSON.parse(request.jsonSolicitacao);
            if (!jsonSolicitacao.cdLinha || jsonSolicitacao.cdLinha !== selectedLineStore?.cdLinha) {
                notification('Essa solicitação não está relacionada com esta linha', {
                    toastId: 'ERROR_REQUEST',
                    type: 'error',
                });
                setRequestProcessValues({});
                setLoadingRequest(false);
                return;
            }
            if (fromClick) {
                if (!formikBag.values.deNome || formikBag.values.deNome === '') {
                    formikBag.setFieldValue('deNome', jsonSolicitacao.condutor?.deNome);
                }
                if (!formikBag.values.deCpf || formikBag.values.deCpf === '') {
                    formikBag.setFieldValue('deCpf', jsonSolicitacao.condutor?.deCpf);
                }
                if (!formikBag.values.deCnh || formikBag.values.deCnh === '') {
                    formikBag.setFieldValue('deCnh', jsonSolicitacao.condutor?.deCnh);
                }
                if (!formikBag.values.tpCategoriaCnh || formikBag.values.tpCategoriaCnh === '') {
                    formikBag.setFieldValue('tpCategoriaCnh', jsonSolicitacao.condutor?.tpCategoriaCnh);
                }
                if (jsonSolicitacao.deCpfCnpj) {
                    const response = await lineEndpoint.getPermissionaryByDocument(jsonSolicitacao.deCpfCnpj);
                    if (response && response.status === 200) {
                        if (response.data !== '') {
                            handleSetSelectedPermissionary([response.data]);
                        }
                    }
                }
                if (jsonSolicitacao.documentos && jsonSolicitacao.documentos.length > 0) {
                    const found = jsonSolicitacao.documentos.filter(
                        (doc) => doc.cdTipoDocumento === DOCUMENT_TYPES.FOTO_3_4,
                    );
                    if (
                        found &&
                        found.length > 0 &&
                        (!formikBag.values.idFileFoto || formikBag.values.idFileFoto === '')
                    ) {
                        formikBag.setFieldValue('idFileFoto', found[0].idFile);
                    }
                }
            }
        }
        setLoadingRequest(false);
    };

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={formInitials}
                initialStatus={{ holding: false }}
                validationSchema={conductorsValidationSchema}
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={handleSubmitForm}>
                {(formProps) => {
                    const { handleSubmit, setFieldValue, values } = formProps;
                    return (
                        <Form>
                            <PageWrapper title="Cadastro / Edição de condutor(es) substituto(s)">
                                {loading && <Loading />}
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={9} container spacing={3}>
                                        <Grid item xs={12} sm={9}>
                                            {loadingLine && <CircularProgress size={16} />}
                                            {!loadingLine && (
                                                <TextField
                                                    variant="filled"
                                                    label="Linha"
                                                    fullWidth
                                                    disabled
                                                    InputLabelProps={{ shrink: true }}
                                                    InputProps={{ readOnly: true }}
                                                    value={selectedLineStore?.deDescricao || ''}
                                                />
                                            )}
                                        </Grid>

                                        <Grid item xs={12}>
                                            {' '}
                                        </Grid>

                                        {(loading || loadingRequest) && (
                                            <Grid item xs={12}>
                                                <CircularProgress size={16} />
                                            </Grid>
                                        )}

                                        {!loading && selectedLineStore && Object.keys(selectedLineStore).length > 0 && (
                                            <RequestProcess
                                                {...props}
                                                {...formProps}
                                                initialState={formInitials}
                                                canEdit={canEdit}
                                                cdLinha={cdLinhaFromPath}
                                                servicesFilter={servicesFilter}
                                                requestProcessValues={requestProcessValues}
                                                setRequestProcessValues={setRequestProcessValues}
                                                setRequestChanged={() => {}}
                                                fieldWidth={6}
                                                callBackRequest={(req, fromClick) =>
                                                    callBackRequest(req, fromClick, formProps)
                                                }
                                            />
                                        )}

                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                component={CustomTextField}
                                                name="carteira"
                                                label="Nº Carteira"
                                                value={carteiraCondutor}
                                                inputProps={{ readOnly: true }}
                                                disabled
                                                loading={loading}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                component={CustomTextField}
                                                label="Nome"
                                                name="deNome"
                                                fullWidth
                                                disabled={loading || !canEdit}
                                                loading={loading}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{
                                                    maxLength: 100,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                component={CustomMaskField}
                                                label="CPF"
                                                name="deCpf"
                                                mask={'999.999.999-99'}
                                                disabled={loading || !canEdit}
                                                loading={loading}
                                                validate={validateDocument}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            {' '}
                                        </Grid>

                                        <Grid item xs={12} sm={3}>
                                            <Field
                                                component={CustomTextField}
                                                label="Nº habilitação"
                                                name="deCnh"
                                                fullWidth
                                                disabled={loading || !canEdit}
                                                loading={loading}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{
                                                    maxLength: 11,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <Field
                                                component={CustomTextField}
                                                label="Categoria"
                                                name="tpCategoriaCnh"
                                                select
                                                loading={loading}
                                                disabled={loading || !canEdit}
                                                SelectProps={{
                                                    native: true,
                                                }}>
                                                <option value="">Selecione...</option>
                                                <option value="A">A</option>
                                                <option value="B">B</option>
                                                <option value="AB">AB</option>
                                                <option value="C">C</option>
                                                <option value="AC">AC</option>
                                                <option value="D">D</option>
                                                <option value="AD">AD</option>
                                                <option value="E">E</option>
                                                <option value="AE">AE</option>
                                            </Field>
                                        </Grid>

                                        <Grid item xs={12} sm={3}>
                                            <Field
                                                component={CustomDateField}
                                                label="Data de emissão"
                                                name="dtEmissao"
                                                disabled={!canEdit}
                                                onChange={(date) => {
                                                    setFieldValue('dtEmissao', date, true);
                                                    setFieldValue(
                                                        'dtValidade',
                                                        moment(date).add(defaultExpirationDate, 'y'),
                                                        true,
                                                    );
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <Field
                                                component={CustomDateField}
                                                label="Data de vencimento"
                                                name="dtValidade"
                                                disabled={!canEdit}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <Box component={'div'} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <ConductorImage {...props} {...formProps} />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <DefaultTable
                                            title="Permissionário(s)"
                                            columns={conductorDetailsPermissionariesTable}
                                            rows={rowsPermissionaries}
                                            onDelete={(e, data) => {
                                                handleDeletePermissionary(e, data);
                                            }}
                                            actionButton={
                                                <AddButton
                                                    color={'primary'}
                                                    className={classes.addButton}
                                                    onClick={() => setOpenPermissionaryDrawer(true)}>
                                                    Adicionar
                                                </AddButton>
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <OtherDocuments
                                            {...props}
                                            title="Anexos"
                                            type={'general'}
                                            othersDocuments={othersDocuments}
                                            setOthersDocuments={setOthersDocuments}
                                            canEdit={canEdit}
                                            disableEditFile={!canEdit}
                                            loadingDocuments={loadingDocuments}
                                            setLoadingDocuments={setLoadingDocuments}
                                            customDeleteDocuments={customDeleteDocuments}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <SaveBox
                                        onCancel={() => history.goBack()}
                                        onSave={() => {
                                            validateFormikForm(formProps, conductorsValidationSchema).then((res) => {
                                                if (Object.keys(res).length > 0) {
                                                    notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                                                        toastId: 'VALIDATION',
                                                        type: 'error',
                                                    });
                                                } else {
                                                    handleSubmit(values);
                                                }
                                            });
                                        }}
                                        firstButtonLabel={'VOLTAR'}
                                        middleButtonLabel={(() => {
                                            if (formProps.status.holding) {
                                                return 'Aguarde...';
                                            }
                                            if (canEdit) {
                                                return 'SALVAR';
                                            }
                                            return null;
                                        })()}
                                        middleButtonDisabled={formProps.status.holding}
                                        lastButtonDisabled={
                                            !canEdit ||
                                            !values.cdCondutorSubstituto ||
                                            selectedConductor?.permissionarios.length === 0
                                        }
                                        lastButtonLabel={'GERAR DOCUMENTO'}
                                        onClickLastButton={() => handleGenerateDocument(values)}
                                    />
                                </Grid>
                            </PageWrapper>
                        </Form>
                    );
                }}
            </Formik>

            <Drawer anchor="right" open={openPermissionaryDrawer} onClose={() => setOpenPermissionaryDrawer(false)}>
                <FindPermissionary
                    isMultiple={true}
                    onClose={() => setOpenPermissionaryDrawer(false)}
                    onSave={handleSetSelectedPermissionary}
                    permissionaries={permissionaries}
                    requestParams={{ boPermissionario: true, cdLinha: cdLinhaFromPath }}
                />
            </Drawer>

            <ConductorModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                data={modalData}
                onUpdate={updateConductor}
                cdLinha={cdLinhaFromPath}
            />
        </>
    );
};

export default ConductorsDetails;
